import { useNavigate } from 'react-router';
import { useSession } from '@hooks/security/use-session';
import { useHistorySummary } from '@hooks/register/history/use-history-summary';
import { useLocalize } from '@hooks/use-localize';
import { DashRegistrationStep } from '@/types/dashboard';
import type { IHistorySummaryModel, IHistorySummarySubmitResult } from '@/types/history/history-summary';
import HistoryProgress from '@views/register/history/history-progress';
import PageLoader from '@components/page-loader';

const HistorySummary = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useHistorySummary(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <HistorySummaryContent model={model} submit={submit} isSubmitting={isSubmitting} submitError={submitError} />
        </PageLoader>
    );
};

interface IHistorySummaryContentProps {
    model: IHistorySummaryModel;
    submit: () => Promise<IHistorySummarySubmitResult>;
    isSubmitting: boolean;
    submitError: string | null;
}

const HistorySummaryContent = ({ model, submit, isSubmitting, submitError }: IHistorySummaryContentProps) => {
    const t = useLocalize(model.languageData);
    const navigate = useNavigate();

    const onConfirm = async () => {
        const result = await submit();

        // The server decides where the donor goes next (pre-workup labs, center
        // select, or pending EMR); the dashboard routes them from there.
        if (result.ok && result.nextStep != null) {
            navigate('/register/donate');
        }
    };

    return (
        <div className="row">
            {model.isIE && (
                <div className="alert alert-warning">
                    {t(
                        'Alert_BrowserRecommend',
                        'To ensure proper function of this site, we recommend that you use Chrome or FireFox.',
                    )}
                    .
                </div>
            )}

            <div className="col-xs-12 col-sm-3 sticky-progress">
                <HistoryProgress model={model} overrideStep={DashRegistrationStep.HistorySummary} />
            </div>

            <div className="col-xs-12 col-sm-8 summary-mobile-content">
                <div className='middle-content'>
                    <h1>{t('DashLabel_AlmostDone', 'Almost done')}!</h1>

                    <h4>
                        {t(
                            'DashLabel_YouVeCompletedAllOfTheRequiredMedicalHistoryIfYouW',
                            "You've completed all of the required medical history. If you would like to go back and review or change any of your answers, please do so now using the navigation on the left",
                        )}
                        .
                    </h4>

                    {submitError && <div className="alert alert-danger">{submitError}</div>}

                    <div className="form-group register-action">
                        <button
                            type="button"
                            className="btn btn-primary btn-lg"
                            style={{ marginTop: 20, maxWidth: 400, width: '100%' }}
                            disabled={isSubmitting}
                            onClick={onConfirm}
                        >
                            {t('DashLabel_IMDoneWithMedicalHistory', "I'm Done with Medical History")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HistorySummary;
